// Company profitability by month with what-if toggles (PF1–PF6, PRD §4.16, rev. 1.20). The API sends
// the raw material: each project's revenue, costs and hours by month, everyone's fixed pay and
// capacity, shared costs, and what a hire of each role costs. The browser switches projects on or
// off and adds hires here, so a toggle needs no round trip (as Capacity does, CM3).
//
// Profit = earned revenue − project costs − people − shared costs, where people are:
//   - fixed: salaried and monthly-fee people, paid whether or not they are staffed;
//   - hourly: hourly people, paid only for the hours planned on switched-on projects;
//   - unfilled: placeholder assignments at their role's standard rate, until a hire fills them;
//   - hires: placeholder hires at their role's target salary (§3.12).

import { addMonths, type Iso, type Month } from "./dates";
import type { LoadThresholds } from "./engine";
import type { ProjectStatus } from "./status";

export type ProfitBasis = "base" | "ote";

/** One assignment's hours and labor by window month, at Base and OTE rates. */
export interface ProfitRow {
  personId: string | null;
  roleId: string;
  hours: number[];
  base: number[];
  ote: number[];
}

export interface ProfitProject {
  id: string;
  code: string;
  name: string;
  clientName: string;
  status: ProjectStatus;
  probability: number;
  renewalOf?: string;
  billable: boolean;
  start: Iso;
  /** The plan's weeks, so a renewal can be copied from here (PF3). */
  weeks: number;
  end: Iso;
  /** Recognized revenue by window month, excluding reimbursables. */
  revenue: number[];
  /** Non-labor costs not billed back, by window month. */
  expenses: number[];
  rows: ProfitRow[];
}

export interface ProfitPerson {
  id: string;
  name: string;
  roleId: string | null;
  /** Capacity hours by window month: working days while active, less approved time off. */
  capacity: number[];
  /** Paid by the hour in that month (hourly employee or hourly 1099): costs only hours worked. */
  hourly: boolean[];
  /** Fixed pay by month (salary or monthly fee, loaded), zero in months they are paid by the hour. */
  fixedBase: number[];
  fixedOte: number[];
}

/** What a hire of a role costs, by window month. */
export interface HireRate {
  /** Loaded target salary ÷ 12 for a full-time hire, or null when the role has no target. */
  targetBase: (number | null)[];
  targetOte: (number | null)[];
  /** The hours a week the target assumes, to scale a part-time hire. */
  targetHoursPerWeek: number;
  /** The role's standard rate per hour, used when there is no target. */
  hourlyBase: number[];
  hourlyOte: number[];
}

export interface ProfitabilityInput {
  months: Month[];
  /** Working days (Mon–Fri less company holidays) in each window month. */
  workingDays: number[];
  thresholds: LoadThresholds;
  roles: { id: string; name: string }[];
  projects: ProfitProject[];
  people: ProfitPerson[];
  /** Shared costs by month: the year's budget ÷ 12, or the month's actuals where entered. */
  shared: number[];
  sharedFromActuals: boolean[];
  hireRates: Record<string, HireRate>;
}

export interface PlaceholderHire {
  id: string;
  roleId: string;
  start: Month;
  hoursPerWeek: number;
  label?: string;
}

export interface ProfitChoices {
  /** Explicit on/off per project; a project not listed takes its status default (PF2). */
  on: Record<string, boolean>;
  weighted: boolean;
  basis: ProfitBasis;
  hires: PlaceholderHire[];
}

/** A saved set of choices (PF6). Points at live projects; never copies a plan. */
export interface ProfitScenario extends ProfitChoices {
  id: string;
  name: string;
  savedAt: string;
  savedBy: string;
}

/** The statuses Profitability lists, and which start on. */
export const PROFIT_STATUSES: readonly ProjectStatus[] = ["booked", "pending", "opportunity", "prospect"];
export const defaultOn = (status: ProjectStatus): boolean => status === "booked" || status === "pending";

export const isOn = (p: Pick<ProfitProject, "id" | "status">, choices: Pick<ProfitChoices, "on">): boolean => choices.on[p.id] ?? defaultOn(p.status);

/** How much of a switched-on project counts: all of it, or its probability when weighted. Booked is always whole. */
export const weightOf = (p: Pick<ProfitProject, "status" | "probability">, weighted: boolean): number => (!weighted || p.status === "booked" ? 1 : p.probability);

/** Where a month's money comes from and goes, one line per source (PF8). Lines sum to the month's totals. */
export type ProfitLineGroup = "revenue" | "projectCosts" | "fixedPeople" | "hourlyPeople" | "unfilled" | "hires" | "shared";

export interface ProfitLine {
  /** Stable across months, so a month can be compared with another line by line. */
  key: string;
  group: ProfitLineGroup;
  label: string;
  projectId?: string;
  personId?: string;
  amount: number;
}

export interface ProfitMonth {
  month: Month;
  lines: ProfitLine[];
  revenue: number;
  projectCosts: number;
  fixedPeople: number;
  hourlyPeople: number;
  unfilled: number;
  hires: number;
  shared: number;
  costs: number;
  profit: number;
  margin: number | null;
  cumulative: number;
  // Staffing (PF4)
  capacity: number;
  demand: number;
  utilization: number | null;
  /** People over the upper load threshold this month, busiest first. */
  over: { personId: string; name: string; load: number | null }[];
  /** Hours on placeholder assignments that no hire absorbs, by role. */
  unfilledHours: Record<string, number>;
  /**
   * hire: the team is over, or a role's unfilled hours exceed its people's spare hours; rebalance:
   * someone is over, or unfilled hours could go to people in the role with room; bench: the team is under.
   */
  flag: "hire" | "rebalance" | "bench" | null;
}

export interface ProjectImpact {
  id: string;
  on: boolean;
  weight: number;
  /** Revenue in the window at the weight it would count with if on. */
  revenue: number;
  /** What it adds to profit in the window: revenue less its project costs, hourly labor and unfilled roles. */
  profit: number;
}

export interface ProfitResult {
  months: ProfitMonth[];
  totals: { revenue: number; costs: number; profit: number; margin: number | null; capacity: number; demand: number };
  projects: ProjectImpact[];
  /** Booked projects ending in the window with no open renewal (PF3). */
  renewalsToConsider: string[];
  /** Months whose profit is below zero. */
  lossMonths: Month[];
}

const zeros = (n: number) => new Array<number>(n).fill(0);

/** Hire hours in a month: hours a week × working days ÷ 5, from its start month. */
function hireCapacity(h: PlaceholderHire, month: Month, workingDays: number): number {
  return month >= h.start ? (h.hoursPerWeek / 5) * workingDays : 0;
}

function hireCost(h: PlaceholderHire, rate: HireRate | undefined, i: number, month: Month, basis: ProfitBasis, workingDays: number): number {
  if (!rate || month < h.start) return 0;
  const target = (basis === "base" ? rate.targetBase : rate.targetOte)[i];
  if (target !== null && target !== undefined && rate.targetHoursPerWeek > 0) return target * (h.hoursPerWeek / rate.targetHoursPerWeek);
  return (basis === "base" ? rate.hourlyBase : rate.hourlyOte)[i] * hireCapacity(h, month, workingDays);
}

export function computeProfitability(input: ProfitabilityInput, choices: ProfitChoices): ProfitResult {
  const n = input.months.length;
  const people = new Map(input.people.map((p) => [p.id, p]));
  const labor = (r: ProfitRow) => (choices.basis === "base" ? r.base : r.ote);

  const revenue = zeros(n);
  const projectCosts = zeros(n);
  const hourlyPeople = zeros(n);
  const demandByPerson = new Map<string, number[]>();
  const unfilledHours = input.months.map(() => new Map<string, number>());
  const unfilledCost = input.months.map(() => new Map<string, number>());
  const lines = input.months.map(() => new Map<string, ProfitLine>());
  const unfilledLines = input.months.map(() => new Map<string, { line: ProfitLine; roleId: string }>());
  const roleName = new Map(input.roles.map((r) => [r.id, r.name]));
  const add = (i: number, line: Omit<ProfitLine, "amount">, amount: number) => {
    if (!amount) return;
    const at = lines[i].get(line.key);
    if (at) at.amount += amount;
    else lines[i].set(line.key, { ...line, amount });
  };

  const impacts: ProjectImpact[] = [];
  for (const p of input.projects) {
    const on = isOn(p, choices);
    const w = weightOf(p, choices.weighted);
    let rev = 0;
    let profit = 0;
    for (let i = 0; i < n; i++) {
      let monthProfit = p.revenue[i] - p.expenses[i];
      rev += p.revenue[i] * w;
      const tag = p.code || p.name;
      if (on) {
        revenue[i] += p.revenue[i] * w;
        projectCosts[i] += p.expenses[i] * w;
        add(i, { key: `revenue:${p.id}`, group: "revenue", label: `${tag} — ${p.name}`, projectId: p.id }, p.revenue[i] * w);
        add(i, { key: `projectCosts:${p.id}`, group: "projectCosts", label: `${tag} — ${p.name}`, projectId: p.id }, p.expenses[i] * w);
      }
      for (const r of p.rows) {
        const hours = r.hours[i] * w;
        const cost = labor(r)[i] * w;
        if (r.personId === null || !people.has(r.personId)) {
          monthProfit -= labor(r)[i];
          if (on && hours) {
            unfilledHours[i].set(r.roleId, (unfilledHours[i].get(r.roleId) ?? 0) + hours);
            unfilledCost[i].set(r.roleId, (unfilledCost[i].get(r.roleId) ?? 0) + cost);
            const key = `unfilled:${p.id}:${r.roleId}`;
            const at = unfilledLines[i].get(key);
            if (at) at.line.amount += cost;
            else unfilledLines[i].set(key, { roleId: r.roleId, line: { key, group: "unfilled", label: `${roleName.get(r.roleId) ?? r.roleId} on ${tag}`, projectId: p.id, amount: cost } });
          }
          continue;
        }
        const hourly = people.get(r.personId)!.hourly[i];
        if (hourly) monthProfit -= labor(r)[i];
        if (!on) continue;
        if (hourly) {
          hourlyPeople[i] += cost;
          add(i, { key: `hourlyPeople:${r.personId}:${p.id}`, group: "hourlyPeople", label: `${people.get(r.personId)!.name} on ${tag}`, projectId: p.id, personId: r.personId }, cost);
        }
        if (hours) {
          const d = demandByPerson.get(r.personId) ?? zeros(n);
          d[i] += hours;
          demandByPerson.set(r.personId, d);
        }
      }
      profit += monthProfit * w;
    }
    impacts.push({ id: p.id, on, weight: w, revenue: rev, profit });
  }

  let cumulative = 0;
  const months: ProfitMonth[] = input.months.map((month, i) => {
    const days = input.workingDays[i];
    // Hires add capacity, which fills their role's unfilled hours first (PF5).
    const hireHoursByRole = new Map<string, number>();
    let hires = 0;
    for (const h of choices.hires) {
      const cost = hireCost(h, input.hireRates[h.roleId], i, month, choices.basis, days);
      hires += cost;
      add(i, { key: `hires:${h.id}`, group: "hires", label: h.label || `${roleName.get(h.roleId) ?? h.roleId} hire from ${h.start}` }, cost);
      hireHoursByRole.set(h.roleId, (hireHoursByRole.get(h.roleId) ?? 0) + hireCapacity(h, month, days));
    }
    let unfilled = 0;
    const left: Record<string, number> = {};
    const share = new Map<string, number>();
    for (const [roleId, hrs] of unfilledHours[i]) {
      const absorbed = Math.min(hrs, hireHoursByRole.get(roleId) ?? 0);
      const remaining = hrs - absorbed;
      share.set(roleId, hrs > 0 ? remaining / hrs : 0);
      unfilled += hrs > 0 ? (unfilledCost[i].get(roleId) ?? 0) * (remaining / hrs) : 0;
      if (remaining > 1e-6) left[roleId] = remaining;
    }
    // Each project's unfilled roles, less what hires absorb, in the same share as the role's total.
    for (const { line, roleId } of unfilledLines[i].values()) add(i, line, line.amount * (share.get(roleId) ?? 0));
    let fixedPeople = 0;
    for (const p of input.people) {
      const cost = choices.basis === "base" ? p.fixedBase[i] : p.fixedOte[i];
      fixedPeople += cost;
      add(i, { key: `fixedPeople:${p.id}`, group: "fixedPeople", label: p.name, personId: p.id }, cost);
    }
    const shared = input.shared[i];
    add(i, { key: "shared", group: "shared", label: input.sharedFromActuals[i] ? "Shared costs (actuals)" : "Shared costs (budget ÷ 12)" }, shared);
    const costs = projectCosts[i] + fixedPeople + hourlyPeople[i] + unfilled + hires + shared;
    const profit = revenue[i] - costs;
    cumulative += profit;

    const hireCap = [...hireHoursByRole.values()].reduce((a, b) => a + b, 0);
    const capacity = input.people.reduce((t, p) => t + p.capacity[i], 0) + hireCap;
    const named = [...demandByPerson.values()].reduce((t, d) => t + d[i], 0);
    const demand = named + [...unfilledHours[i].values()].reduce((a, b) => a + b, 0);
    const utilization = capacity > 0 ? demand / capacity : demand > 0 ? null : 0;
    const over = input.people
      .flatMap((p) => {
        const d = demandByPerson.get(p.id)?.[i] ?? 0;
        if (!d) return [];
        const load = p.capacity[i] > 0 ? d / p.capacity[i] : null;
        return load === null || load > input.thresholds.over + 1e-9 ? [{ personId: p.id, name: p.name, load }] : [];
      })
      .sort((a, b) => (b.load ?? Infinity) - (a.load ?? Infinity));
    // Unfilled hours need a hire only beyond the spare hours of people already in that role.
    const short = Object.entries(left).some(([roleId, hrs]) => {
      const spare = input.people.filter((p) => p.roleId === roleId).reduce((t, p) => t + Math.max(0, p.capacity[i] - (demandByPerson.get(p.id)?.[i] ?? 0)), 0);
      return hrs > spare + 1e-6;
    });
    const flag =
      utilization === null || utilization > input.thresholds.over + 1e-9 || short
        ? "hire"
        : over.length || Object.keys(left).length
          ? "rebalance"
          : capacity > 0 && utilization < input.thresholds.under - 1e-9
          ? "bench"
          : null;
    return {
      month,
      lines: [...lines[i].values()],
      revenue: revenue[i],
      projectCosts: projectCosts[i],
      fixedPeople,
      hourlyPeople: hourlyPeople[i],
      unfilled,
      hires,
      shared,
      costs,
      profit,
      margin: revenue[i] ? profit / revenue[i] : null,
      cumulative,
      capacity,
      demand,
      utilization,
      over,
      unfilledHours: left,
      flag,
    };
  });

  const sum = (f: (m: ProfitMonth) => number) => months.reduce((t, m) => t + f(m), 0);
  const totalRevenue = sum((m) => m.revenue);
  const totalProfit = sum((m) => m.profit);
  return {
    months,
    totals: { revenue: totalRevenue, costs: sum((m) => m.costs), profit: totalProfit, margin: totalRevenue ? totalProfit / totalRevenue : null, capacity: sum((m) => m.capacity), demand: sum((m) => m.demand) },
    projects: impacts,
    renewalsToConsider: renewalsToConsider(input),
    lossMonths: months.filter((m) => m.profit < -1e-6).map((m) => m.month),
  };
}

/** Booked projects that end inside the window and that no open project renews (PF3). */
export function renewalsToConsider(input: Pick<ProfitabilityInput, "months" | "projects">): string[] {
  if (!input.months.length) return [];
  const first = `${input.months[0]}-01`;
  const after = `${addMonths(input.months.at(-1)!, 1)}-01`;
  const renewed = new Set(input.projects.map((p) => p.renewalOf).filter(Boolean));
  return input.projects.filter((p) => p.status === "booked" && p.end >= first && p.end < after && !renewed.has(p.id)).map((p) => p.id);
}

// --- Scenarios (PF6) ---------------------------------------------------------------------------------

const isMonth = (v: unknown): v is Month => typeof v === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

export function validateChoices(c: Partial<ProfitChoices>, roles: ReadonlySet<string>): string[] {
  const errors: string[] = [];
  if (typeof c.on !== "object" || c.on === null || Array.isArray(c.on) || Object.values(c.on).some((v) => typeof v !== "boolean")) errors.push("Say which projects are on.");
  if (typeof c.weighted !== "boolean") errors.push("Say whether projects are weighted by probability.");
  if (c.basis !== "base" && c.basis !== "ote") errors.push("Rates are Base or OTE.");
  if (!Array.isArray(c.hires)) errors.push("Hires must be a list.");
  else {
    if (c.hires.length > 50) errors.push("A scenario holds up to 50 hires.");
    for (const h of c.hires) {
      if (typeof h?.id !== "string" || !h.id) errors.push("Every hire needs an id.");
      if (!roles.has(h?.roleId)) errors.push("Every hire needs a role from the list.");
      if (!isMonth(h?.start)) errors.push("Every hire needs a start month.");
      if (typeof h?.hoursPerWeek !== "number" || !(h.hoursPerWeek > 0 && h.hoursPerWeek <= 60)) errors.push("A hire works 1 to 60 hours a week.");
      if (h?.label !== undefined && (typeof h.label !== "string" || h.label.length > 80)) errors.push("A hire's label is up to 80 characters.");
    }
  }
  return [...new Set(errors)];
}

// --- Month breakdown and what changed (PF8) ----------------------------------------------------------

export const COST_GROUPS: readonly ProfitLineGroup[] = ["projectCosts", "fixedPeople", "hourlyPeople", "unfilled", "hires", "shared"];

export interface ComparedLine extends Omit<ProfitLine, "amount"> {
  amount: number;
  /** The same line in the month compared with; 0 when it had none. */
  before: number;
  /** What the line does to profit: revenue up is positive, a cost up is negative. */
  effect: number;
}

/**
 * Lines of `months` added up (one month, or a span), set against the same lines of `compare`.
 * Every line either side appears, so a project that ends shows as revenue falling to zero.
 */
export function compareLines(months: readonly ProfitMonth[], compare: readonly ProfitMonth[] = []): ComparedLine[] {
  const out = new Map<string, ComparedLine>();
  const fold = (ms: readonly ProfitMonth[], side: "amount" | "before") => {
    for (const m of ms)
      for (const l of m.lines) {
        const at = out.get(l.key) ?? { key: l.key, group: l.group, label: l.label, projectId: l.projectId, personId: l.personId, amount: 0, before: 0, effect: 0 };
        at[side] += l.amount;
        out.set(l.key, at);
      }
  };
  fold(months, "amount");
  fold(compare, "before");
  for (const l of out.values()) l.effect = (l.group === "revenue" ? 1 : -1) * (l.amount - l.before);
  return [...out.values()];
}
